// SVG sparkline. Smooth catmull-rom-ish line with a soft gradient fill.
// Color follows direction of move.

export default function Sparkline({
  data = [],
  width = 800,
  height = 160,
  padding = 8,
  positive = true,
}) {
  if (!data || data.length < 2) {
    const midY = height / 2;
    const x1 = padding;
    const x2 = width - padding;
    return (
      <svg
        className="sparkline sparkline--loading"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="loading"
      >
        <line
          x1={x1}
          y1={midY}
          x2={x2}
          y2={midY}
          stroke="var(--gold-deep)"
          strokeWidth="1.2"
          strokeDasharray="4 6"
          opacity="0.25"
        />
        <circle r="3.5" cy={midY} fill="var(--gold-deep)">
          <animate
            attributeName="cx"
            values={`${x1};${x2};${x1}`}
            dur="2.4s"
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
          />
          <animate
            attributeName="opacity"
            values="0;1;1;0"
            keyTimes="0;0.15;0.85;1"
            dur="2.4s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    );
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || max || 1;

  const innerW = width - padding * 2;
  const innerH = height - padding * 2;

  const points = data.map((v, i) => {
    const x = padding + (i / (data.length - 1)) * innerW;
    const y = padding + (1 - (v - min) / range) * innerH;
    return [x, y];
  });

  // Smooth path through points using midpoint quadratic curves
  const smoothPath = (() => {
    let d = `M ${points[0][0].toFixed(2)} ${points[0][1].toFixed(2)}`;
    for (let i = 1; i < points.length; i++) {
      const [px, py] = points[i - 1];
      const [cx, cy] = points[i];
      const mx = (px + cx) / 2;
      const my = (py + cy) / 2;
      d += ` Q ${px.toFixed(2)} ${py.toFixed(2)} ${mx.toFixed(2)} ${my.toFixed(
        2
      )}`;
      if (i === points.length - 1) {
        d += ` T ${cx.toFixed(2)} ${cy.toFixed(2)}`;
      }
    }
    return d;
  })();

  const fillPath = `${smoothPath} L ${points[points.length - 1][0].toFixed(
    2
  )} ${height - padding} L ${points[0][0].toFixed(2)} ${
    height - padding
  } Z`;

  const last = points[points.length - 1];
  const lineColor = positive ? 'var(--gold-deep)' : 'var(--red)';
  const fillColor = positive ? 'var(--gold)' : 'var(--red)';
  const gradId = `sparkFill-${positive ? 'up' : 'down'}`;

  return (
    <svg
      className="sparkline"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={fillColor} stopOpacity="0.32" />
          <stop offset="100%" stopColor={fillColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#${gradId})`} />
      <path
        d={smoothPath}
        fill="none"
        stroke={lineColor}
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle
        cx={last[0]}
        cy={last[1]}
        r="3.2"
        fill={lineColor}
        stroke="var(--bg)"
        strokeWidth="1.5"
      />
    </svg>
  );
}
